<template>
  <platform-modal
    v-model="showModal"
    icon="user"
    :width="500"
    :title="$t('modal.organisationUser.edit.title')"
    :cancel="true"
    :delete-disabled="isLastUser"
    :delete-disabled-tooltip="isLastUser ? $t('modal.organisationUser.edit.lastUserCannotBeDeleted') : undefined"
    @close="$emit('close')"
    @delete="confirmRemove"
  >
    <div class="d-flex flex-column align-center py-4">
      <platform-avatar
        entity-type="user"
        :entity="userEntity"
        :size="80"
        class="mb-4"
      />
      <h3 class="mb-2">{{ organisationUser.user?.displayName || $t('modal.organisationUser.edit.unknownUser') }}</h3>
      <p class="grey--text mb-2">{{ organisationUser.user?.email || '' }}</p>
      <p class="text-caption grey--text">
        {{ $t('modal.organisationUser.edit.createdOn', [formatDate(organisationUser.createdAt)]) }}
      </p>
    </div>

    <confirmation-modal
      v-if="confirmingRemove"
      action="delete"
      force-icon="user"
      entity-type="user"
      :entity="userEntity"
      :message="deleteConfirmationMessage"
      @confirm="remove"
      @cancel="confirmingRemove = false"
    />
  </platform-modal>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import PlatformModal from '@/components/PlatformModal.vue'
import PlatformAvatar from '@/components/shared/PlatformAvatar.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import API from '@/services/API'
import dayjs from 'dayjs'

interface OrganisationUserWithDetails {
  id: string
  organisationId: string
  cognitoIdentityId: string
  createdAt: string
  updatedAt: string
  user: {
    id: string
    cognitoId: string | null
    displayName: string | null
    email: string | null
  } | null
}

export default Vue.extend({
  name: 'EditOrganisationUserModal',
  components: {
    PlatformModal,
    PlatformAvatar,
    ConfirmationModal
  },
  props: {
    organisationUser: { type: Object as PropType<OrganisationUserWithDetails>, required: true },
    organisationName: { type: String, required: true },
    organisationUserCount: { type: Number, required: true }
  },
  data () {
    return {
      showModal: true,
      confirmingRemove: false,
      pendingRemove: false
    }
  },
  computed: {
    userEntity (): { id: string, displayName?: string, email?: string } {
      return {
        id: this.organisationUser.cognitoIdentityId,
        displayName: this.organisationUser.user?.displayName ?? undefined,
        email: this.organisationUser.user?.email ?? undefined
      }
    },
    deleteConfirmationMessage (): string {
      return this.$t(
        'modal.organisationUser.edit.deleteConfirmation',
        [this.organisationUser.user?.displayName || this.organisationUser.user?.email, this.organisationName]
      ) as string
    },
    isLastUser (): boolean {
      return this.organisationUserCount === 1
    }
  },
  watch: {
    showModal (val: boolean): void {
      if (!val) {
        this.$emit('close')
      }
    }
  },
  methods: {
    formatDate (dateString: string): string {
      return dayjs(dateString).format('DD MMM YYYY')
    },
    confirmRemove (): void {
      this.confirmingRemove = true
    },
    async remove (): Promise<void> {
      this.pendingRemove = true

      try {
        await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'removeOrganisationUser',
            organisationUserId: this.organisationUser.id
          }
        })

        this.showModal = false
        this.$emit('removed')
      } catch (error: any) {
        console.error('Failed to remove organisation user:', error)
        const errorMessage = error?.response?.data?.error || this.$t('modal.organisationUser.edit.deleteError')
        this.$toasted.error(errorMessage)
      } finally {
        this.pendingRemove = false
        this.confirmingRemove = false
      }
    }
  }
})
</script>
